import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Typography, Divider, Paper, Grid, Box, Stack, Tooltip, IconButton, Button
} from '@mui/material';
import { useSnackbar } from 'notistack';
import Modal from 'components/Modal';
import useConfirm from 'core/hooks/useConfirm';
import EditIcon from '@mui/icons-material/Edit';
import EditUser from './edit';
import ReactDataGrid from '@inovua/reactdatagrid-community'
import '@inovua/reactdatagrid-community/index.css'
import { getStudentList, activateStudent, deactivateStudent } from 'services/students';
import { reactDataGridExportCSV } from 'components/reactdata-grid';
import DownloadIcon from '@mui/icons-material/Download';

const StudentsPage = () => {

  const gridStyle = useMemo(() => ({ minHeight: 480, width: '100%' }), []);
  const [gridRef, setGridRef] = useState(null);
  const [studentList, setStudentList] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const confirm = useConfirm();
  const [userInfo, setUserInfo] = useState({ info: null });

  const onActionBtnClick = useCallback((e, type, userData) => {
    e.preventDefault();
    if(type === 'edit'){
      editStudentDetails(userData);
    }
  }, []);

  const defaultFilterValue = [
    { name: 'firstName', operator: 'contains', type: 'string', value: '' },
    { name: 'email', operator: 'contains', type: 'string', value: '' },
    { name: 'mobile', operator: 'contains', type: 'string', value: '' },
    { name: 'createdOn', operator: 'contains', type: 'string', value: '' },
  ];
  const columns = useMemo(() => ([
    { name: 'firstName', header: 'Name', width: 300},
    { name: 'email', header: 'Email', width: 320},
    { name: 'mobile', header: 'Mobile', width: 150},
    { name: 'createdOn', header: 'Created On', width: 150},
    { name: 'actions', header: 'Actions', width: 220, render: ({data}) => {
      return (
        <Stack direction="row" spacing={1}>
          <Box>
            <Tooltip title="Edit Student Details" arrow placement="left">
              <IconButton onClick={(e) => onActionBtnClick(e, 'edit', data)}>
                <EditIcon color="warning" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box>
            <Tooltip title={data?.active ? 'Deactivate Student' : 'Activate Student'} arrow placement="left">
              <Button
                onClick={() => changeUserState(data?.userId, data?.active)} 
                size="small" 
                variant="contained"
                sx={{textTransform: 'none'}}
                color={data?.active ? 'error' : 'success'}
                >
                  {data?.active ? 'Deactivate' : 'Activate'}
              </Button>
            </Tooltip>
          </Box>
        </Stack>
      )
    }},
  ]),[onActionBtnClick]);

  useEffect(() => {
    getStudentsData();
  }, []);

  function getStudentsData() {
    getStudentList().then(({response, error}) => {
      if(response){
        setStudentList([...(response || [])]);
      } else {
        setStudentList([]);
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  const editStudentDetails = (userData) => {
    setUserInfo({ info: userData });
    setTimeout(() => setOpenModal(true), 100);
  }

  const changeUserState = (userId, isActive) => {
    confirm({ title: `${isActive ? 'Deactivate Student' : 'Activate Student'}`, description: 'Are you sure you want to proceed?' })
    .then(() => { 
      if(isActive){
        _deactivateStudent(userId);
      }else{
        _activateStudent(userId);
      }
    }).catch(() => {});
  }

  function _activateStudent(userId) {
    activateStudent({userId}).then(({response, error}) => {
      if(response){
        enqueueSnackbar("Student activated successfully", {variant: 'success'});
        getStudentsData();
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  function _deactivateStudent(userId) {
    deactivateStudent({userId}).then(({response, error}) => {
      if(response){
        enqueueSnackbar("Student deactivated successfully", {variant: 'success'});
        getStudentsData();
      } else {
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  const onModalClose = useCallback(
    () => {
      setOpenModal(false);
    },
    [setOpenModal],
  )

 
  const onSuccess = useCallback(
    () => {
      setOpenModal(false);
      getStudentsData();
      setUserInfo({id: null});
    },
    [setOpenModal],
  )

  return (
    <Paper sx={{ minWidth: 275, py: 2, px: 3, minHeight: "calc(100vh - 100px )" }}>
    {/* <CardContent> */}
      <Typography component="h2" variant="h6" color="secondary" gutterBottom sx={{mb: 1}}>
        Students
      </Typography>
      <Divider />
      <Box noValidate  sx={{ mt: 2 }} >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={12} lg={12} textAlign="right" >
            <Button onClick={() => reactDataGridExportCSV(gridRef, 'students.csv') } startIcon={<DownloadIcon />} variant="outlined" color="success" sx={{textTransform: 'none'}}>Export CSV</Button>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} >
            <Box component="div" sx={{  maxHeight: 720, width: '100%' }}>
              <ReactDataGrid
                handle={setGridRef}
                // idProperty="index"
                style={gridStyle}
                defaultFilterValue={defaultFilterValue}
                columns={columns}
                dataSource={studentList}
                rowHeight={null} 
                minRowHeight={50}
              />
            </Box>
          </Grid>
        </Grid>
      </Box> 
      <Modal  maxWidth={"sm"} open={openModal} onModalClose={onModalClose} title={'Edit Student'}>
        <EditUser  userData={userInfo.info} onSuccess={onSuccess} onFailure={onModalClose} />
      </Modal>
    </Paper>
  );
}
 
export default React.memo(StudentsPage);
